import { Feature } from 'ol';
import { WKT } from 'ol/format';
import { Point} from 'ol/geom';
import * as toastr from "toastr";
import { createEmpty, extend } from 'ol/extent';
import { get as getProj, fromLonLat, getPointResolution } from 'ol/proj';
import { GridReferences } from '../Utility/GridReferences';
import { IAPIResult } from '../Models/UtilityModels';
import { FeatureType, ICAMSKey, TextPack } from '../Models/UtilityModels';
import { Utilities } from '../Utility/Utilities';
import { MainMap } from './MainMap';

// MAIN CLASS
export class MapZoom {

    // ----------------------------------------------------------- //
    // GLOBAL VARS

    // ----------------------------------------------------------- //
    //Constructor
    public constructor(protected mainMap: MainMap) {
        
    }
    
    //PUBLIC Functions
    public zoomToMyLocation = (clearHighlights: boolean): void => {
        var self = this;

        let onSuccess = function (position) {
            let coords = fromLonLat([position.coords.longitude, position.coords.latitude], self.mainMap.mapConfigModel.mapInitialState.mapBaseProjection);
            let point = new Point(coords);
            let postitionFeature = new Feature(point);
                
            //Zoom map
            //self.map.getView().fit(extent, { duration: 200 });
            self.mainMap.map.getView().animate({ center: coords, zoom: 17 }, { duration: 200 });

            if (self.mainMap.locationLayer.getSource().getFeatures().length > 0) {
                //always try to remove feature first
                self.mainMap.locationLayer.getSource().removeFeature(postitionFeature);
            }
            self.mainMap.locationLayer.getSource().addFeature(postitionFeature);

            window.setTimeout(function () {
                if (self.mainMap.locationLayer.getSource().getFeatures().length > 0) {
                    self.mainMap.locationLayer.getSource().removeFeature(postitionFeature);
                }
            }, 15000);
        };

        let onError = function (error) {
            Utilities.GetTranslations([{ textPack: TextPack.OfficerMapTextPack, key: self.mainMap.textPackKeys.OfficerMapTextPack_TS_Toastr_LocationError }]).then(function (r) { toastr.error(r.data[0].value); }).catch(function (e) { console.log(e); });
            console.log("GeoLocation error: code=" + error.code + ", message=" + error.message);
        };

        navigator.geolocation.getCurrentPosition(onSuccess, onError, null);
     
    }

    public zoomToFeatureAlreadyInMap = (data: ICAMSKey, clearHighlights: boolean): void => {
        let feature = this.mainMap.getFeatureFromMap(data);

        // Clear selected features
        let selectedCollection = this.mainMap.MapInteraction.selectInteraction.getFeatures();
        if (clearHighlights == true) {
            this.mainMap.highlightedFeaturesList = [];
            selectedCollection.clear();
        }

        // Zoom to feature
        if (feature != null) {
            // Select the feature
            this.mainMap.highlightedFeaturesList.push(feature);
            selectedCollection.push(feature);

            // Zoom to the feature
            this.mainMap.forceReloadHighlightLayer();
            this.centreMapOnFeatures([feature]);
        }
    }

    public zoomToLocationAndThenHighlightFeature = (data: ICAMSKey, clearHighlights: boolean, callback: Function): void => {
        // Clear all current highlights
        const self = this;
       
        if (data == null) {
            toastr.error("Error! No geometry was found.");
            return;
        }

        this.mainMap.highlightedFeaturesList = [];
        let selectedCollection = this.mainMap.MapInteraction.selectInteraction.getFeatures();
        selectedCollection.clear();

        // If the feature exists, just zoom to it
        let feature = this.mainMap.getFeatureFromMap(data);
        if (feature != null) {
            this.zoomToFeatureAlreadyInMap(data, clearHighlights);
            if (callback!=null) callback(feature);
            return;
        }
   
        // Zoom the map
        var wktStr = data.wkt;
        if (wktStr == null) {
            $.post('/Utility/GetWKTFromFeatureCode', data,
                function (result: IAPIResult) {
                    try {
                        if (result.success == true) {
                            wktStr = result.data;
                            var outFeature = doZoom();
                            if (callback != null) callback(outFeature);
                        }
                        else {
                            Utilities.GetTranslations([{ textPack: TextPack.OfficerMapTextPack, key: self.mainMap.textPackKeys.OfficerMapTextPack_TS_Toastr_WktError }]).then(function (r) { toastr.error(r.data[0].value); }).catch(function (e) { console.log(e); });
                            if (callback != null) callback(null);
                        }
                    }
                    catch (err) {
                        console.log(err);
                        if (callback != null) callback(null);
                    }
                }
            ).fail(function (xhr) {
                if (xhr.status === 401) {//redirect to login in unauthorised
                    window.location.href = '/account/login?ReturnUrl=' + encodeURIComponent(window.location.pathname);
                }
            });
        }
        else {
            var outFeature = doZoom();
            if (callback != null) callback(outFeature);
        }

        function doZoom() {
            let wkt = new WKT();
            let tmpFeature = wkt.readFeature(wktStr, {
                dataProjection: 'EPSG:27700',
                featureProjection: self.mainMap.mapConfigModel.mapInitialState.mapBaseProjection
            });

            // Select the feature
            self.mainMap.highlightedFeaturesList.push(tmpFeature);
            selectedCollection.push(tmpFeature);

            // Zoom to the feature
            self.mainMap.forceReloadHighlightLayer();
            self.centreMapOnFeatures([tmpFeature]);

            return tmpFeature;
        }
    }

    public zoomToWKTandHighlight = (wktArr: Array<string>, clearHighlights: boolean, addWKTToMap: boolean, featureType: FeatureType): void => {
        let features: Array<Feature> = [];
        const self = this;

        wktArr.forEach(wktStr => {
            try {
                if (wktStr != null && wktStr != undefined && wktStr != "") {
                    let wkt = new WKT();
                    let f = wkt.readFeature(wktStr, {
                        dataProjection: 'EPSG:27700',
                        featureProjection: self.mainMap.mapConfigModel.mapInitialState.mapBaseProjection
                    });
                    features.push(f);
                }
            }
            catch (e) {
                console.log("Could not create feature");
                Utilities.GetTranslations([{ textPack: TextPack.OfficerMapTextPack, key: self.mainMap.textPackKeys.OfficerMapTextPack_TS_Toastr_FeatureZoomError }]).then(function (r) { toastr.error(r.data[0].value); }).catch(function (e) { console.log(e); });
            }
        });

        try {

            // Clear selected features
            let selectedCollection = this.mainMap.MapInteraction.selectInteraction.getFeatures();
            if (clearHighlights == true) {
                this.mainMap.highlightedFeaturesList = [];
                selectedCollection.clear();
            }

            // Zoom to feature
            if (features.length > 0)
            {
                if (addWKTToMap == true) {
                    // Select the feature
                    this.mainMap.highlightedFeaturesList = features;
                    selectedCollection = features as any;
                }
                
                // Zoom to the feature
                this.mainMap.forceReloadHighlightLayer();
                this.centreMapOnFeatures(features);

                // Refresh map 
                self.mainMap.refreshMapLayer(featureType);
            }
        }
        catch (e) {
            Utilities.GetTranslations([{ textPack: TextPack.OfficerMapTextPack, key: self.mainMap.textPackKeys.OfficerMapTextPack_TS_Toastr_FeatureZoomError }]).then(function (r) { toastr.error(r.data[0].value); }).catch(function (e) { console.log(e); });
        }
    }

    public zoomToLatLon = (data: ICAMSKey, clearHighlights: boolean, placeMarker: boolean): void => {
        const self = this;
     
        try {
            // Clear selected features
            // Clear selected features
            let selectedCollection = this.mainMap.MapInteraction.selectInteraction.getFeatures();
            if (clearHighlights == true) {
                this.mainMap.highlightedFeaturesList = [];
                selectedCollection.clear();
            }

            let centre = fromLonLat([parseFloat(data.lon), parseFloat(data.lat)], self.mainMap.mapConfigModel.mapInitialState.mapBaseProjection); // longitude first, then latitude
            self.mainMap.map.getView().animate({ center: centre, zoom: data.zoom }, { duration: 200 });

            if (placeMarker == true) {
                var tmpFeature = new Feature(new Point(centre));
                this.mainMap.highlightedFeaturesList.push(tmpFeature);
                selectedCollection.push(tmpFeature);
                // Zoom to the feature
                this.mainMap.forceReloadHighlightLayer();
            }
        }
        catch (e) {
            console.log(e);
            Utilities.GetTranslations([{ textPack: TextPack.OfficerMapTextPack, key: self.mainMap.textPackKeys.OfficerMapTextPack_TS_Toastr_LatLonZoomError }]).then(function (r) { toastr.error(r.data[0].value); }).catch(function (e) { console.log(e); });
        }
    }

    public zoomToGridRef = (data: any, clearHighlights: boolean, placeMarker: boolean): void => {
        const self = this;
        try {
            let osgb = new GridReferences.OSGB();
            if (osgb.ParseGridRef(data.gridRef)) {
                // Clear selected features
                let selectedCollection = this.mainMap.MapInteraction.selectInteraction.getFeatures();
                if (clearHighlights == true) {
                    this.mainMap.highlightedFeaturesList = [];
                    selectedCollection.clear();
                }

                let latLon = osgb.GetWGS84();
                let centre = fromLonLat([latLon.Longitude, latLon.Latitude], self.mainMap.mapConfigModel.mapInitialState.mapBaseProjection); // longitude first, then latitude
                self.mainMap.map.getView().animate({ center: centre, zoom: data.zoom }, { duration: 200 });

                if (placeMarker == true) {
                    var tmpFeature = new Feature(new Point(centre));
                    this.mainMap.highlightedFeaturesList.push(tmpFeature);
                    selectedCollection.push(tmpFeature);
                    // Zoom to the feature
                    this.mainMap.forceReloadHighlightLayer();
                }

            }
            else throw 'Cannot parse grid ref';
        }
        catch (e) {
            console.log(e);
            Utilities.GetTranslations([{ textPack: TextPack.OfficerMapTextPack, key: self.mainMap.textPackKeys.OfficerMapTextPack_TS_Toastr_GridRefZoomError }]).then(function (r) { toastr.error(r.data[0].value); }).catch(function (e) { console.log(e); });
        }
    }

    public zoomToScale = (scale: number): void => {
        const self = this;

        const scaleResolution =
            scale /
            getPointResolution(
                self.mainMap.map.getView().getProjection(),
                90.864 / 25.4,
                self.mainMap.map.getView().getCenter()
            );
        self.mainMap.map.getView().setResolution(scaleResolution);

        // Hack to set the scale text
        setTimeout(function () { $('.ol-scale-text').html("1 : " + (scale * 1000).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")); }, 0);
    }

    public centreMapOnFeatures = (features: Array<Feature>, zoom?: number): void => {
        const self = this;
        // Get the max extent
        let extent = createEmpty();
        features.forEach(f => {
            extent = extend(extent, f.getGeometry().getExtent());
        });

        // Now calc what padding to put around the view. 
        // For mobile devices where width < 700px, focus on the bottom centre
        // Otherwise zoom to the middle right of the screen
        let width = $(window).width();
        let height = $(window).height();
        let padding = []; // top, right, bottom, left
        if (width < 700) padding = [height - height / 2, width * 0.15, height * 0.15, width * 0.15 + 60];
        else if (this.areRightPanelsOpen() == true) padding = [height * 0.15, width * 0.15, height * 0.15, width * 0.15]
        else padding = [height * 0.15, width * 0.15, height * 0.15, width - width / 2 + 60];

        // If zoom is not specified, then zoom to the extent of the features
        if (zoom == undefined || zoom == null) {
            self.mainMap.map.getView().fit(extent, {
                duration: 200, padding: padding, callback: function () {
                    //for (var layer in self.mapLayersList) self.mapLayersList[layer].getSource().refresh();
                }
            });
        }
        else {
            //let centre = getCenter(extent);
            //self.map.getView().animate({ center: centre, zoom: zoom }, { duration: 200 });
            self.mainMap.map.getView().fit(extent, {
                duration: 200, padding: padding, maxZoom: zoom, callback: function () {
                    //for (var layer in self.mapLayersList) self.mapLayersList[layer].getSource().refresh();
                }
            });
        }
    }

    private areRightPanelsOpen = (): boolean => {
        if ($('#CAMSMainPage_RightPanels #treeResultsPanel').hasClass('show') == true) return true;
        if ($('#CAMSMainPage_RightPanels #searchPanel').hasClass('show') == true) return true;
    }
}




